import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import useNewBuildAvailable from '@/hooks/useNewBuildAvailable';
import useMovable from './useMovable';
import styles from './NewVersionBanner.module.scss';

/** Same stroke style as the Alert glyphs: 24 grid, 1.5 stroke, round caps. */
const RefreshGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M20.25 12a8.25 8.25 0 1 1-8.25-8.25c2.3 0 4.5.93 6.18 2.51l2.07 1.99" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M20.25 3.75v4.5h-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Tells the one person who cannot know it that the page they are looking at is out
 * of date. Until this existed, a deploy reached only those who happened to reload.
 *
 * HAT-1440: a white card in the anatomy of the site's Alert (accent, icon, title, body),
 * with a line on what reloading costs. It floats over the page, so it can be moved by its
 * handle, or pushed past a side edge into a small tab that brings it back on click.
 *
 * SPR-10013: every merge to master is a deploy, so on a busy day a page goes stale every
 * few minutes, and a card that came back each time the tab regained focus kept breaking
 * into the work. Now the update mostly takes care of itself: once the tab is stale, the
 * next move to another screen loads that screen from the server instead of rendering it
 * in place - the coordinator was leaving the page anyway, so nothing open is lost. Until
 * then all there is to see is the small tab on the edge, which opens the card on click,
 * and "not now" tucks the card back into it for good.
 */
const NewVersionBanner = () => {
  const isStale = useNewBuildAvailable();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const cardRef = useRef<HTMLDivElement>(null);
  const tabRef = useRef<HTMLButtonElement>(null);
  const { position, docked, dockPreview, isDragging, tuck, undock, handleProps } = useMovable(cardRef, { startTucked: true });

  // The path only, not the query: a filter or a tab on the same screen is not leaving it.
  // history.state survives the reload, so a screen handed state by navigate() still gets it.
  const renderedPath = useRef(pathname);
  useEffect(() => {
    if (isStale && pathname !== renderedPath.current) window.location.reload();
    renderedPath.current = pathname;
  }, [isStale, pathname]);

  // Tucked away from the keyboard, the handle that had focus is gone - focus follows to the tab.
  const isDocked = docked !== null;
  useEffect(() => {
    if (isDocked && (!document.activeElement || document.activeElement === document.body)) tabRef.current?.focus();
  }, [isDocked]);

  if (!isStale) return null;

  if (docked) {
    return (
      <button
        ref={tabRef}
        type="button"
        className={`${styles.tab} ${docked.side === 'left' ? styles.tabLeft : styles.tabRight}`}
        style={{ top: docked.y }}
        onClick={undock}
        aria-label={t('newVersionShow')}
        title={t('newVersionShow')}
      >
        <RefreshGlyph />
        <span className={styles.tabDot} aria-hidden="true" />
      </button>
    );
  }

  const className = [styles.banner, position && styles.moved, isDragging && styles.dragging, dockPreview && styles.aboutToTuck].filter(Boolean).join(' ');

  return (
    // polite and not assertive: worth reading, not worth interrupting a screen
    // reader mid-sentence for.
    <div ref={cardRef} className={className} style={position ? { left: position.x, top: position.y } : undefined} role="status" aria-live="polite">
      <span className={styles.accent} aria-hidden="true" />

      <span className={styles.icon} aria-hidden="true">
        <RefreshGlyph />
      </span>

      <div className={styles.text}>
        <span className={`${styles.title} body-md-semibold`}>{t('newVersionAvailable')}</span>
        <span className={`${styles.hint} body-sm-regular`}>{t('newVersionHint')}</span>
      </div>

      <div className={styles.actions}>
        <button type="button" className={`${styles.reload} body-sm-semibold`} onClick={() => window.location.reload()}>
          {t('newVersionReload')}
        </button>
        <button type="button" className={`${styles.later} body-sm-regular`} onClick={tuck}>
          {t('newVersionLater')}
        </button>
      </div>

      <button type="button" className={styles.grip} aria-label={t('newVersionMove')} title={t('newVersionMove')} {...handleProps}>
        <svg width="12" height="18" viewBox="0 0 12 18" aria-hidden="true">
          {[3, 9, 15].flatMap((cy) => [3, 9].map((cx) => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1.5" fill="currentColor" />))}
        </svg>
      </button>
    </div>
  );
};

export default NewVersionBanner;
